/**
 * Design — aba de personalizacao da pagina publica do formulario.
 *
 * Esquerda: controles agrupados (pagina, cartao, cabecalho, campos, botao,
 * celular, rodape). Direita: preview ao vivo usando o MESMO componente da
 * pagina publica (PublicFormView), com alternancia desktop/celular.
 *
 * Persistencia: `config.design` do formulario (PATCH). So o que difere do
 * padrao e' salvo (designParaSalvar) — o backend filtra de novo
 * (normalizar_design), entao valor invalido volta como erro de API.
 *
 * Imagens entram por URL (logo, banner, fundo): o gestor ja hospeda no
 * proprio site/Drive. Upload de arquivo fica para quando houver pedido.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiExternalLink,
  FiMonitor,
  FiRotateCcw,
  FiSave,
  FiSmartphone,
} from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { buscarCep } from "../../services/enderecos";
import {
  atualizarFormulario,
  obterFormulario,
  urlPublicaFormulario,
} from "../../services/formularios";
import { PageContainer, PageState } from "../../styles/shared";

import FormTabs from "./FormTabs";
import PublicFormView from "./PublicFormView";
import {
  DESIGN_PADRAO,
  FONTES,
  OPCOES_DESIGN,
  designEhPadrao,
  designParaSalvar,
  normalizarDesign,
} from "./designConfig";
import {
  ColorRow,
  DesignControls,
  DesignLayout,
  DesignPreview,
  DesignPreviewBar,
  DesignViewport,
  EditorCard,
  EditorCardBody,
  EditorCardHead,
  EditorFieldset,
  FieldStack,
  SettingsGrid,
} from "./styles";

// ------------------------------------------------------------
// Controles (cada um le/escreve UMA chave do design)
// ------------------------------------------------------------

function CampoNumero({ design, set, chave, label, min, max, sufixo = "px", full }) {
  const id = `design-${chave}`;
  return (
    <FieldStack className={full ? "full" : undefined}>
      <label className="input-label" htmlFor={id}>
        {label}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          id={id}
          className="input"
          type="number"
          min={min}
          max={max}
          value={design[chave]}
          onChange={(e) => set(chave, e.target.value === "" ? DESIGN_PADRAO[chave] : Number(e.target.value))}
          style={{ maxWidth: 110 }}
        />
        <span className="input-help">{sufixo}</span>
      </div>
    </FieldStack>
  );
}

// Cor opcional: vazio = "deixar o tema decidir". O <input type=color> nao
// sabe ficar vazio, entao o botao "×" limpa a escolha explicitamente.
function CampoCor({ design, set, chave, label, ajuda }) {
  const valor = design[chave] || "";
  return (
    <FieldStack>
      <span className="input-label">{label}</span>
      <ColorRow>
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(valor) ? valor : "#888888"}
          onChange={(e) => set(chave, e.target.value)}
          aria-label={label}
        />
        <span className="hex">{valor || "padrão do tema"}</span>
        {valor && (
          <button
            type="button"
            className="btn btn--ghost"
            style={{ height: 26, padding: "0 8px" }}
            onClick={() => set(chave, "")}
            title="Voltar ao padrão do tema"
          >
            ×
          </button>
        )}
      </ColorRow>
      {ajuda && <span className="input-help">{ajuda}</span>}
    </FieldStack>
  );
}

function CampoSelect({ design, set, chave, label, opcoes }) {
  const id = `design-${chave}`;
  return (
    <FieldStack>
      <label className="input-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="select"
        value={design[chave]}
        onChange={(e) => set(chave, e.target.value)}
      >
        {opcoes.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </FieldStack>
  );
}

function CampoTexto({ design, set, chave, label, placeholder, maxLength, type = "text", ajuda, full }) {
  const id = `design-${chave}`;
  return (
    <FieldStack className={full ? "full" : undefined}>
      <label className="input-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        type={type}
        value={design[chave] || ""}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => set(chave, e.target.value)}
      />
      {ajuda && <span className="input-help">{ajuda}</span>}
    </FieldStack>
  );
}

function CampoBool({ design, set, chave, label, full }) {
  return (
    <FieldStack className={full ? "full" : undefined}>
      <label
        className="input-help"
        style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", color: "var(--ink)", fontSize: 13 }}
      >
        <input
          type="checkbox"
          checked={Boolean(design[chave])}
          onChange={(e) => set(chave, e.target.checked)}
          style={{ width: 16, height: 16 }}
        />
        {label}
      </label>
    </FieldStack>
  );
}

function Secao({ titulo, children }) {
  return (
    <EditorCard>
      <EditorCardHead>
        <h2>{titulo}</h2>
      </EditorCardHead>
      <EditorCardBody>
        <SettingsGrid>{children}</SettingsGrid>
      </EditorCardBody>
    </EditorCard>
  );
}

// ------------------------------------------------------------
// Pagina
// ------------------------------------------------------------

export default function Design() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions } = useAuth();
  // Salvar o design exige formulario:manage no backend; sem ela, so ver (FORM-41).
  const podeEditar = hasAllPermissions(["formulario:manage"]);
  // Mesmo comportamento da pagina publica: o preview nao mente.
  const buscarCepDoPreview = useCallback((cep) => buscarCep(request, cep), [request]);

  const [formulario, setFormulario] = useState(null);
  const [design, setDesign] = useState(() => normalizarDesign({}));
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [viewport, setViewport] = useState("desktop");

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const dado = await obterFormulario(request, id);
      setFormulario(dado);
      setDesign(normalizarDesign(dado.config?.design));
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar o formulário.");
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const set = useCallback((chave, valor) => {
    setDesign((prev) => ({ ...prev, [chave]: valor }));
  }, []);

  const salvar = async () => {
    const atualizado = await atualizarFormulario(request, id, {
      config: { ...(formulario?.config || {}), design: designParaSalvar(design) },
    });
    setFormulario(atualizado);
    setDesign(normalizarDesign(atualizado?.config?.design));
  };

  const restaurar = () => setDesign(normalizarDesign({}));

  const salvo = formulario?.config?.design;
  const alterado = useMemo(
    () => JSON.stringify(designParaSalvar(design)) !== JSON.stringify(designParaSalvar(salvo)),
    [design, salvo]
  );

  if (loading) {
    return (
      <PageContainer>
        <PageState>Carregando formulário...</PageState>
      </PageContainer>
    );
  }
  if (erro || !formulario) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate("/comercial/formularios")}
          backLabel="Voltar"
          title="Formulário"
        />
        <PageState $tone="error">{erro || "Formulário não encontrado."}</PageState>
      </PageContainer>
    );
  }

  const ctl = { design, set };

  return (
    <PageContainer style={{ maxWidth: "none" }}>
      <EntityHeader
        onBack={() => navigate("/comercial/formularios")}
        backLabel="Formularios"
        title={formulario.nome || "Formulario sem nome"}
        subtitle="Design da página pública — o que o visitante vê em /f/{slug}"
        badges={[
          alterado
            ? { key: "pendente", label: "Alterações não salvas", tone: "warning" }
            : { key: "ok", label: "Salvo", tone: "neutral" },
        ]}
        actions={[
          formulario.slug && formulario.ativo
            ? {
                key: "abrir",
                label: "Abrir página pública",
                icon: <FiExternalLink />,
                onClick: () =>
                  window.open(urlPublicaFormulario(formulario.slug), "_blank", "noopener"),
              }
            : null,
        ].filter(Boolean)}
      />

      <FormTabs id={id} ativa="design" />

      <DesignLayout>
        <DesignControls>
          {podeEditar ? (
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={restaurar}
                disabled={designEhPadrao(design)}
                title="Volta todos os controles ao padrão (salve para aplicar)"
              >
                <FiRotateCcw aria-hidden="true" /> Restaurar padrão
              </button>
              <AsyncButton
                variant="primary"
                onClick={salvar}
                disabled={!alterado}
                successMessage="Design salvo."
                silentError
              >
                <FiSave size={14} aria-hidden="true" /> Salvar design
              </AsyncButton>
            </div>
          ) : (
            <div className="so-banner info" role="note">
              <span>
                Você pode ver o design, mas não alterá-lo: falta a permissão
                para gerenciar formulários.
              </span>
            </div>
          )}

          <EditorFieldset disabled={!podeEditar}>
            <Secao titulo="Página">
              <CampoCor {...ctl} chave="pagina_bg" label="Cor de fundo da página" />
              <CampoNumero {...ctl} chave="pagina_padding" label="Espaço acima do cartão" min={0} max={200} />
              <CampoTexto
                {...ctl}
                chave="pagina_bg_imagem"
                label="Imagem de fundo (URL)"
                type="url"
                placeholder="https://..."
                maxLength={500}
                full
              />
              <CampoSelect {...ctl} chave="pagina_bg_ajuste" label="Ajuste da imagem" opcoes={OPCOES_DESIGN.pagina_bg_ajuste} />
              <CampoSelect
                {...ctl}
                chave="fonte_familia"
                label="Fonte"
                opcoes={FONTES.map((f) => [f.value, f.label])}
              />
              <CampoNumero {...ctl} chave="fonte_base" label="Tamanho base da fonte" min={11} max={24} />
            </Secao>

            <Secao titulo="Cartão do formulário">
              <CampoNumero {...ctl} chave="cartao_largura" label="Largura máxima" min={320} max={1400} />
              <CampoCor {...ctl} chave="cartao_bg" label="Cor de fundo do cartão" />
              <CampoNumero {...ctl} chave="cartao_padding" label="Espaçamento interno" min={0} max={120} />
              <CampoNumero {...ctl} chave="cartao_raio" label="Cantos arredondados" min={0} max={60} />
              <CampoSelect {...ctl} chave="cartao_sombra" label="Sombra" opcoes={OPCOES_DESIGN.cartao_sombra} />
              <CampoBool {...ctl} chave="cartao_borda" label="Borda fina no cartão" />
              <CampoBool {...ctl} chave="barra_topo" label="Barra colorida no topo da página" full />
            </Secao>

            <Secao titulo="Cabeçalho">
              <CampoTexto
                {...ctl}
                chave="logo_url"
                label="Logo (URL da imagem)"
                type="url"
                placeholder="https://.../logo.png"
                maxLength={500}
                full
              />
              <CampoNumero {...ctl} chave="logo_altura" label="Altura do logo" min={16} max={240} />
              <CampoSelect {...ctl} chave="logo_alinhamento" label="Alinhamento do logo" opcoes={OPCOES_DESIGN.logo_alinhamento} />
              <CampoTexto
                {...ctl}
                chave="imagem_topo_url"
                label="Imagem de topo / banner (URL)"
                type="url"
                placeholder="https://.../banner.jpg"
                maxLength={500}
                ajuda="Aparece acima do título, na largura do cartão."
                full
              />
              <CampoCor {...ctl} chave="titulo_cor" label="Cor do título" />
              <CampoNumero {...ctl} chave="titulo_tamanho" label="Tamanho do título" min={12} max={72} />
              <CampoCor {...ctl} chave="texto_cor" label="Cor do texto" ajuda="Subtítulo, rótulos e rodapé." />
            </Secao>

            <Secao titulo="Campos">
              <CampoNumero {...ctl} chave="gap_campos" label="Espaço entre os campos" min={0} max={60} />
              <CampoSelect
                {...ctl}
                chave="colunas"
                label="Colunas"
                opcoes={[
                  [2, "Duas (campos de meia largura lado a lado)"],
                  [1, "Uma (tudo empilhado)"],
                ]}
              />
              <CampoNumero {...ctl} chave="input_raio" label="Cantos dos campos" min={0} max={40} />
              <CampoCor {...ctl} chave="input_bg" label="Fundo dos campos" />
              <CampoCor {...ctl} chave="label_cor" label="Cor dos rótulos" />
            </Secao>

            <Secao titulo="Botão de envio">
              <CampoTexto {...ctl} chave="botao_texto" label="Texto do botão" placeholder="Enviar" maxLength={60} />
              <CampoSelect {...ctl} chave="botao_largura" label="Largura" opcoes={OPCOES_DESIGN.botao_largura} />
              <CampoSelect {...ctl} chave="botao_alinhamento" label="Alinhamento" opcoes={OPCOES_DESIGN.botao_alinhamento} />
              <CampoNumero {...ctl} chave="botao_raio" label="Cantos do botão" min={0} max={40} />
              <CampoCor {...ctl} chave="botao_cor" label="Cor do botão" ajuda="Vazio = cor do tema (Editor)." />
              <CampoCor {...ctl} chave="botao_texto_cor" label="Cor do texto do botão" />
            </Secao>

            <Secao titulo="Celular e rodapé">
              <CampoBool {...ctl} chave="empilhar_no_celular" label="Empilhar os campos em uma coluna no celular" full />
              <CampoNumero {...ctl} chave="mobile_padding" label="Margem no celular" min={0} max={80} />
              <FieldStack className="full">
                <label className="input-label" htmlFor="design-rodape_texto">
                  Texto do rodapé
                </label>
                <textarea
                  id="design-rodape_texto"
                  className="textarea"
                  rows={2}
                  maxLength={300}
                  value={design.rodape_texto || ""}
                  placeholder="Ex.: Seus dados ficam só com a gente. Respondemos em até 1 dia útil."
                  onChange={(e) => set("rodape_texto", e.target.value)}
                />
              </FieldStack>
            </Secao>
          </EditorFieldset>

          <p className="input-help" style={{ margin: 0 }}>
            O design vale para a página hospedada <code>/f/{formulario.slug}</code> e
            para quem a incorpora por iframe. O HTML colado direto no site do
            cliente herda o CSS do site.
          </p>
        </DesignControls>

        <DesignPreview>
          <DesignPreviewBar>
            <span className="titulo">Pré-visualização</span>
            <div className="modos" role="group" aria-label="Tamanho da tela">
              <button
                type="button"
                className={viewport === "desktop" ? "is-on" : ""}
                onClick={() => setViewport("desktop")}
                aria-pressed={viewport === "desktop"}
              >
                <FiMonitor aria-hidden="true" /> Desktop
              </button>
              <button
                type="button"
                className={viewport === "celular" ? "is-on" : ""}
                onClick={() => setViewport("celular")}
                aria-pressed={viewport === "celular"}
              >
                <FiSmartphone aria-hidden="true" /> Celular
              </button>
            </div>
          </DesignPreviewBar>
          <DesignViewport $mobile={viewport === "celular"}>
            <PublicFormView
              dados={formulario}
              design={design}
              readOnly
              preview
              viewport={viewport}
              previewKey={`${viewport}-${formulario.campos?.length || 0}`}
              buscarCep={buscarCepDoPreview}
            />
          </DesignViewport>
        </DesignPreview>
      </DesignLayout>
    </PageContainer>
  );
}
